'use client';
import type { FormEvent } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { useSearchContext } from '../context';
import { getText } from '@/utils/text/text';
import { SvgLoader } from '@/app/elements/svg/loader';
import { useStore } from '@/store/store';
import { trackSearchStarted, trackSearchInitialized } from '@/tracking/amplitude/events/general/search-events';
import { SEARCH } from '@/utils/constants';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import { EVENT_TYPE, getSnowplowEvent } from '@/tracking/snowplow/utils';
import { ddStartVital, ddStopVital, ddCancelVital } from '@/utils/datadog';
import { SEARCH_VITALS, SEARCH_VITAL_DESCRIPTIONS } from '@/utils/datadog/vitals';

import './styles/@theme';

export const SearchField: React.FC = () => {
    const { setQuery, clearQuery, query: contextQuery } = useSearchContext();
    const [localValue, setLocalValue] = useState(contextQuery ?? '');
    const hasStartedSearch = useRef(false);
    const previousQuery = useRef<string | undefined>(contextQuery);
    const inputRef = useRef<HTMLInputElement>(null);
    /** Length of last debounced `contextQuery` for Snowplow Search Initialized (fire on crossing MIN_QUERY_LENGTH). */
    const previousDebouncedLenForSnowplow = useRef(-1);
    const { sendEvent } = useStore((state) => state.tracking);

    const debouncedSetQuery = useDebounce(
        useCallback(
            (value: string) => {
                setQuery(value);
            },
            [setQuery]
        )
    );

    const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
        const { value } = event.target as HTMLInputElement;
        const wasEmpty = localValue.length === 0;
        setLocalValue(value);

        // track 'search started' on first keystroke
        if (wasEmpty && value.length > 0 && !hasStartedSearch.current) {
            hasStartedSearch.current = true;
            ddCancelVital(SEARCH_VITALS.RECOMMENDED_READY);
            sendEvent([{ destination: 'amplitude', eventProps: trackSearchStarted(value) }]);
        }

        if (value.length >= SEARCH.MIN_QUERY_LENGTH) {
            ddStartVital(
                SEARCH_VITALS.QUERY_RESPONSE,
                {
                    description: SEARCH_VITAL_DESCRIPTIONS[SEARCH_VITALS.QUERY_RESPONSE],
                    context: { query_length: value.length }
                },
                { replace: true }
            );
        } else {
            ddCancelVital(SEARCH_VITALS.QUERY_RESPONSE);
        }

        debouncedSetQuery(value);
    };

    // track 'search initialized' when debounced query is set
    useEffect(() => {
        const wasEmpty = !previousQuery.current || previousQuery.current.length === 0;
        const hasValue = contextQuery && contextQuery.length > 0;

        if (wasEmpty && hasValue) {
            sendEvent([{ destination: 'amplitude', eventProps: trackSearchInitialized(contextQuery) }]);
        }

        previousQuery.current = contextQuery;
    }, [contextQuery, sendEvent]);

    // Snowplow Search Initialized: spec = 3+ characters on debounced query (not the same as Amplitude "any non-empty")
    useEffect(() => {
        const q = contextQuery ?? '';
        const len = q.length;

        if (len === 0) {
            previousDebouncedLenForSnowplow.current = -1;
            return;
        }

        const crossedMinLength =
            len >= SEARCH.MIN_QUERY_LENGTH && previousDebouncedLenForSnowplow.current < SEARCH.MIN_QUERY_LENGTH;

        if (crossedMinLength) {
            const firstThree = q.slice(0, SEARCH.MIN_QUERY_LENGTH).toLowerCase();
            void (async () => {
                try {
                    await ensureCoreEventsInitialized();
                    const snowplowPayload = getSnowplowEvent(EVENT_TYPE.SEARCH_INITIALIZED, {
                        eventCategory: 'search',
                        firstThreeCharacters: firstThree
                    });
                    sendEvent([{ destination: 'snowplow', eventProps: snowplowPayload }]);
                } catch {
                    // Logged in ensureCoreEventsInitialized
                }
            })();
        }

        previousDebouncedLenForSnowplow.current = len;
    }, [contextQuery, sendEvent]);

    // reset hasStartedSearch when search is cleared
    useEffect(() => {
        if (!contextQuery) {
            hasStartedSearch.current = false;
        }

        setLocalValue(contextQuery || '');
    }, [contextQuery]);

    const resetField = () => {
        ddStopVital(SEARCH_VITALS.QUERY_RESPONSE, {
            context: { cancelled: true, cancel_reason: 'cleared' }
        });
        clearQuery();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
        }
    };

    return (
        <div className="search-input-field">
            <SvgLoader iconName="search-magnifying-glass" />
            <input
                data-testid="input"
                ref={inputRef}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                name="query"
                value={localValue}
                minLength={SEARCH.MIN_QUERY_LENGTH}
                placeholder={getText({ text: 'components.search.placeholder' })}
                autoComplete="off"
            />
            {contextQuery && contextQuery.length > 0 && (
                <button data-testid="reset-button" onClick={resetField}>
                    <SvgLoader iconName="close" />
                </button>
            )}
        </div>
    );
};
